/**
 * ValuesFromChoice — the chooser for where a criterion's values come from.
 *
 * Drawn on the track's Criteria & scales tab, one per criterion (migration 1062).
 * Five square choices with their one-line meanings (no native select, by the
 * owner's rule), and, for "one person", the workspace's members to name the person.
 * The caller saves; this only chooses.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { VALUES_FROM, valuesFromLabel, type ValuesFrom } from '@/lib/valuesFrom';

export interface ValuesFromChoiceProps {
  value: ValuesFrom | null;
  personId: string | null;
  /** Members who may be named for "one person": id and display name. */
  members: ReadonlyArray<{ userId: string; name: string }>;
  disabled?: boolean;
  busy?: boolean;
  onChange: (next: { from: ValuesFrom | null; personId: string | null }) => void;
}

export function ValuesFromChoice({ value, personId, members, disabled, busy, onChange }: ValuesFromChoiceProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const personName = useMemo(() => members.find((m) => m.userId === personId)?.name ?? null, [members, personId]);
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q ? members.filter((m) => m.name.toLowerCase().includes(q)) : members;
    return list.slice(0, 8);
  }, [members, filter]);

  const summary = valuesFromLabel(value, personName);

  if (disabled) {
    return <span style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', color: value ? 'var(--text-pri)' : 'var(--text-ter)' }}>{summary}</span>;
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-expanded={open}
        data-testid="values-from-button"
        style={{
          background: 'none', border: '1px solid var(--rule)', borderRadius: '2px', padding: '4px 8px',
          fontFamily: 'var(--sans)', fontSize: '0.8rem', cursor: busy ? 'wait' : 'pointer',
          color: value ? 'var(--text-pri)' : 'var(--mustard)',
        }}
      >
        {summary}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Where do this criterion's values come from?"
          style={{
            position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, width: '340px',
            background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px', padding: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: '4px',
          }}
        >
          {VALUES_FROM.map((a) => {
            const selected = a.id === value;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  if (a.id === 'specific_person') {
                    onChange({ from: a.id, personId });
                    return; // stays open so the person can be named
                  }
                  onChange({ from: a.id, personId: null });
                  setOpen(false);
                }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px', textAlign: 'left', width: '100%',
                  background: selected ? 'var(--hover-tint)' : 'none', border: '1px solid', borderColor: selected ? 'var(--text-sec)' : 'var(--rule)',
                  borderRadius: '2px', padding: '6px 8px', cursor: 'pointer',
                }}
              >
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontFamily: 'var(--sans)', fontSize: '0.85rem', color: 'var(--text-pri)', fontWeight: 500 }}>{a.label}</span>
                  <span style={{ display: 'block', fontFamily: 'var(--sans)', fontSize: '0.72rem', color: 'var(--text-sec)' }}>{a.meaning}</span>
                </span>
                {selected && <Check size={14} style={{ color: 'var(--green)', marginTop: '2px' }} />}
              </button>
            );
          })}
          {value === 'specific_person' && (
            <div style={{ borderTop: '1px solid var(--rule)', marginTop: '4px', paddingTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Who?"
                aria-label="Name the person"
                style={{ fontFamily: 'var(--sans)', fontSize: '0.8rem', padding: '4px 6px', border: '1px solid var(--rule)', borderRadius: '2px', background: 'var(--surface)', color: 'var(--text-pri)' }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {shown.map((m) => (
                  <button
                    key={m.userId}
                    type="button"
                    onClick={() => { onChange({ from: 'specific_person', personId: m.userId }); setOpen(false); }}
                    style={{
                      fontFamily: 'var(--sans)', fontSize: '0.75rem', padding: '3px 8px', borderRadius: '2px', cursor: 'pointer',
                      border: '1px solid', borderColor: m.userId === personId ? 'var(--text-sec)' : 'var(--rule)',
                      background: m.userId === personId ? 'var(--hover-tint)' : 'none', color: 'var(--text-pri)',
                    }}
                  >
                    {m.name}
                  </button>
                ))}
                {shown.length === 0 && <span style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-ter)' }}>No member by that name.</span>}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => { onChange({ from: null, personId: null }); setOpen(false); }}
            style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: '4px 2px', fontFamily: 'var(--sans)', fontSize: '0.72rem', color: 'var(--text-ter)', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Leave it unsaid
          </button>
        </div>
      )}
    </div>
  );
}
